import { useState } from 'react';
import { PencilIcon } from '@heroicons/react/24/outline';
import { geneticsApi, type MutationAllele } from '../../api/genetics';

interface EditMutationAlleleFormProps {
  allele: MutationAllele;
  onSaved: () => void;
  onCancel: () => void;
}

export default function EditMutationAlleleForm({
  allele,
  onSaved,
  onCancel,
}: EditMutationAlleleFormProps) {
  const [name, setName] = useState(allele.name);
  const [description, setDescription] = useState(allele.phenotypeDescription ?? '');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async () => {
    if (!name.trim()) {
      setError('Name is required.');
      return;
    }

    setSaving(true);
    setError(null);
    try {
      await geneticsApi.updateAllele(allele.id, {
        name: name.trim(),
        phenotypeDescription: description.trim() || undefined,
      });
      onSaved();
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to update allele');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="mt-1 rounded border border-dashed border-indigo-200 bg-indigo-50/50 p-2 space-y-2">
      <p className="text-xs font-medium text-slate-700">Edit allele</p>
      {error && <p className="text-xs text-red-600">{error}</p>}
      <div className="grid grid-cols-1 gap-2">
        <p className="text-xs text-slate-500 font-mono">{allele.symbol}</p>
        <input
          type="text"
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="Name"
          className="rounded border border-slate-300 px-2 py-1 text-xs"
        />
      </div>
      <input
        type="text"
        value={description}
        onChange={(e) => setDescription(e.target.value)}
        placeholder="Phenotype description (optional)"
        className="w-full rounded border border-slate-300 px-2 py-1 text-xs"
      />
      <div className="flex gap-2">
        <button
          type="button"
          onClick={() => void handleSubmit()}
          disabled={saving}
          className="inline-flex items-center gap-1 rounded bg-indigo-600 px-2 py-1 text-xs text-white hover:bg-indigo-700 disabled:opacity-50"
        >
          <PencilIcon className="h-3.5 w-3.5" />
          {saving ? 'Saving…' : 'Save'}
        </button>
        <button type="button" onClick={onCancel} className="text-xs text-slate-600 hover:text-slate-800">
          Cancel
        </button>
      </div>
    </div>
  );
}
